import {
  useCallback,
  useEffect,
  useId,
  useRef,
  type ReactNode,
} from 'react'
import { createPortal } from 'react-dom'
import styles from './RangoModal.module.css'

const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])'

type Props = {
  open: boolean
  title: ReactNode
  children?: ReactNode
  /** Rodapé de ações (Fixed Bar) — ex.: Cancelar + Confirmar. */
  footer?: ReactNode
  onClose: () => void
  closeLabel?: string
  /** Rótulo acessível do diálogo quando o título é visual (ex.: ícone). */
  ariaLabel?: string
  tone?: 'default' | 'danger'
  /** Fecha ao clicar no backdrop (padrão: true). */
  dismissOnBackdrop?: boolean
  /** Elemento que abriu o modal (recebe o foco de volta ao fechar). */
  opener?: HTMLElement | null
}

/**
 * Modal Rango DS — raio `$border-radius-lg` (24px), padding `$spacing-5x`,
 * heading + slot + footer de botões.
 *
 * Acessibilidade: `role="dialog"` + `aria-modal`, foco preso (Tab/Shift+Tab
 * em ciclo), Escape fecha, foco volta ao elemento que abriu ao fechar.
 */
export function RangoModal({
  open,
  title,
  children,
  footer,
  onClose,
  closeLabel = 'Fechar',
  ariaLabel,
  tone = 'default',
  dismissOnBackdrop = true,
  opener: openerProp,
}: Props) {
  const titleId = useId()
  const dialogRef = useRef<HTMLDivElement>(null)
  // Gatilho de abertura: prop explícita ou o último elemento focado antes da
  // montagem (o `autofocus` do primeiro campo pode chegar antes dos effects).
  const openerRef = useRef<HTMLElement | null>(null)
  if (open && !openerRef.current) {
    openerRef.current = openerProp ?? (document.activeElement as HTMLElement | null)
  }

  // Foco inicial: primeiro `[data-autofocus]`, senão o primeiro focável.
  // (Mesma ordem do ACUL: o `autofocus` nativo já pode ter movido o foco.)
  useEffect(() => {
    if (!open) return
    const dialog = dialogRef.current
    if (!dialog) return
    const first =
      dialog.querySelector<HTMLElement>('[data-autofocus]') ??
      dialog.querySelector<HTMLElement>(FOCUSABLE) ??
      dialog
    first.focus()
    const opener = openerRef.current
    return () => {
      openerRef.current = null
      if (opener && opener.isConnected) opener.focus()
    }
  }, [open])

  const onKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLDivElement>) => {
      if (event.key === 'Escape') {
        event.stopPropagation()
        onClose()
        return
      }
      if (event.key !== 'Tab') return
      const dialog = dialogRef.current
      if (!dialog) return
      const nodes = Array.from(
        dialog.querySelectorAll<HTMLElement>(FOCUSABLE),
      ).filter((el) => !el.hasAttribute('disabled'))
      if (nodes.length === 0) {
        event.preventDefault()
        dialog.focus()
        return
      }
      const first = nodes[0]
      const last = nodes[nodes.length - 1]
      const current = document.activeElement
      if (event.shiftKey && (current === first || !dialog.contains(current))) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && current === last) {
        event.preventDefault()
        first.focus()
      }
    },
    [onClose],
  )

  if (!open) return null

  const dialog = (
    <div
      className={styles.backdrop}
      data-testid="rango-modal-backdrop"
      onMouseDown={(event) => {
        if (dismissOnBackdrop && event.target === event.currentTarget) onClose()
      }}
    >
      <div
        ref={dialogRef}
        className={styles.dialog}
        data-tone={tone}
        role="dialog"
        aria-modal="true"
        aria-labelledby={ariaLabel ? undefined : titleId}
        aria-label={ariaLabel}
        tabIndex={-1}
        onKeyDown={onKeyDown}
        data-testid="rango-modal"
      >
        <div className={styles.header}>
          <h2 id={titleId} className={styles.title}>
            {title}
          </h2>
          <button
            type="button"
            className={styles.close}
            aria-label={closeLabel}
            onClick={onClose}
          >
            <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true">
              <path
                d="M6.5 6.5l11 11M17.5 6.5l-11 11"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
              />
            </svg>
          </button>
        </div>
        {children ? <div className={styles.slot}>{children}</div> : null}
        {footer ? <div className={styles.footer}>{footer}</div> : null}
      </div>
    </div>
  )

  // Portal para o body: o card das telas ACUL tem contexto de empilhamento
  // próprio e um modal dentro dele ficaria preso no fluxo do formulário.
  return typeof document === 'undefined'
    ? dialog
    : createPortal(dialog, document.body)
}
